import Emittery from 'emittery';

import { Waypoint } from 'model/Order';
import { QuoteRequest, QuoteResponse } from 'model/Quote';
import { Vehicle } from 'model/Vehicle';

import { StripeCustomer } from './bff';
import { processNextUrl } from './navigation';
import { session } from './session';
import { gtagService } from '../services/gtag-service';

interface SessionEvent<T> {
  type: keyof typeof SessionEventEnum;
  message: T;
}

interface PaymentEvents {
  orderAlreadyProcessed: undefined;
  orderFailed: undefined;
  submitPayment: undefined;
  addressCompleted: boolean;
  paymentCompleted: boolean;
  paymentError: string | undefined;
  termsAccepted: boolean;
}

interface FlatWaypoint extends Omit<Waypoint, 'date'> {
  date?: string;
}

interface WidthEvent extends SessionEvent<number> {
  type: SessionEventEnum.WIDTH;
  message: number;
}

export const pageTitle: Record<string, string> = {
  '/': 'Get Quote',
  '/sdk': 'Get Quote',
  '/sdk/:brand': 'Get Quote',

  '/sdk/quote': 'Review Quote',
  '/sdk/:brand/quote': 'Review Quote',
  '/quote': 'Review Quote',

  '/customer-and-vehicle': 'Customer and Vehicle Info',
  '/pickup': 'Pickup Info',
  '/delivery': 'Delivery Info',
  '/payment': 'Payment Info',
  '/order-success': 'Order Success',
  '/payment-success': 'Order Success',
  '/payment-failure': 'Payment Failure',
  '/order-rejected': 'Order Failure'
};

export enum SessionEventEnum {
  WIDTH = 'WIDTH',
  HEIGHT = 'HEIGHT',
  QUOTE_REQUESTED = 'QUOTE_REQUESTED',
  QUOTE_RECEIVED = 'QUOTE_RECEIVED',
  QUOTE_ACCEPTED = 'QUOTE_ACCEPTED',
  QUOTE_REJECTED = 'QUOTE_REJECTED',
  QUOTE_BOOKED = 'QUOTE_BOOKED',
  QUOTE_NEW = 'QUOTE_NEW',
  NEW_QUOTE_AFTER_PAYMENT = 'NEW_QUOTE_AFTER_PAYMENT',
  CUSTOMER_AND_VEHICLE_INFO = 'CUSTOMER_AND_VEHICLE_INFO',
  PICKUP_INFO = 'PICKUP_INFO',
  DELIVERY_INFO = 'DELIVERY_INFO',
  PAYMENT_SUBMITTED = 'PAYMENT_SUBMITTED',
  PAYMENT_SUCCESS = 'PAYMENT_SUCCESS',
  PAYMENT_FAILURE = 'PAYMENT_FAILURE',
  ORDER_SUBMITTED = 'ORDER_SUBMITTED',
  ORDER_SUCCESS = 'ORDER_SUCCESS',
  ORDER_FAILURE = 'ORDER_FAILURE',
  PRICE_UPDATED = 'PRICE_UPDATED',
  TRAILER_TYPE_UPDATED = 'TRAILER_TYPE_UPDATED',
  UPDATE_VEHICLE_INFO = 'UPDATE_VEHICLE_INFO'
}

export const SESSION_SCREEN: Record<keyof typeof SessionScreenEnum, string> = {
  REQUEST_QUOTE: '/',
  SDK_REQUEST_QUOTE: '/sdk',
  SDK_BRAND_REQUEST_QUOTE: '/sdk/:brand',

  SDK_REVIEW_QUOTE: '/sdk/quote',
  SDK_BRAND_REVIEW_QUOTE: '/sdk/:brand/quote',
  REVIEW_QUOTE: '/quote',

  SDK_SUMMARY: '/sdk/summary',
  SDK_BRAND_SUMMARY: '/sdk/:brand/summary',

  CUSTOMER_AND_VEHICLE_INFO: '/customer-and-vehicle',
  PICKUP_INFO: '/pickup',
  DELIVERY_INFO: '/delivery',
  PAYMENT_INFO: '/payment',
  ORDER_SUCCESS: '/order-success',
  PAYMENT_SUCCESS: '/payment-success',
  PAYMENT_FAILURE: '/payment-failure',
  ORDER_FAILURE: '/order-rejected'
};

export enum SessionScreenEnum {
  REQUEST_QUOTE = 'REQUEST_QUOTE',
  SDK_REQUEST_QUOTE = 'SDK_REQUEST_QUOTE',
  SDK_BRAND_REQUEST_QUOTE = 'SDK_BRAND_REQUEST_QUOTE',

  SDK_REVIEW_QUOTE = 'SDK_REVIEW_QUOTE',
  SDK_BRAND_REVIEW_QUOTE = 'SDK_BRAND_REVIEW_QUOTE',
  REVIEW_QUOTE = 'REVIEW_QUOTE',

  SDK_SUMMARY = 'SDK_SUMMARY',
  SDK_BRAND_SUMMARY = 'SDK_BRAND_SUMMARY',

  CUSTOMER_AND_VEHICLE_INFO = 'CUSTOMER_AND_VEHICLE_INFO',
  PICKUP_INFO = 'PICKUP_INFO',
  DELIVERY_INFO = 'DELIVERY_INFO',
  PAYMENT_INFO = 'PAYMENT_INFO',
  ORDER_SUCCESS = 'ORDER_SUCCESS',
  PAYMENT_SUCCESS = 'PAYMENT_SUCCESS',
  PAYMENT_FAILURE = 'PAYMENT_FAILURE',
  ORDER_FAILURE = 'ORDER_REJECTED'
}

export const emitter = new Emittery<PaymentEvents>();

// TODO: refactor this
const createEventClient = () => {
  let lastHeight = 0;

  const emitSessionEvent = <T = never, X = T>(
    event: SessionEvent<T>,
    xfmr?: (event: T) => X
  ): void => {
    const xfmdEvent: SessionEvent<T | X> = {
      type: event.type,
      message: xfmr ? xfmr(event.message) : event.message
    };

    const isSdk = session.getSdk() !== null;

    if (event.type !== SessionEventEnum.HEIGHT) {
      gtagService.dispatch({
        eventName: 'session',
        input: {
          eventName: event.type.toLowerCase()
        }
      });
    }

    if (window.parent !== window && isSdk) {
      // @ts-expect-error - TODO FIX wrong type
      processNextUrl(xfmdEvent.type);
    } else {
      window.parent.postMessage(xfmdEvent, { targetOrigin: '*' });
    }
  };

  const emitQuoteRequested = (message: QuoteRequest) =>
    emitSessionEvent({
      type: SessionEventEnum.QUOTE_REQUESTED,
      message
    });

  const emitQuoteReceived = (message: QuoteResponse) =>
    emitSessionEvent({
      type: SessionEventEnum.QUOTE_RECEIVED,
      message
    });

  const emitQuoteAccepted = () =>
    emitSessionEvent({
      type: SessionEventEnum.QUOTE_ACCEPTED,
      message: undefined
    });

  const emitQuoteRejected = () =>
    emitSessionEvent({
      type: SessionEventEnum.QUOTE_REJECTED,
      message: undefined
    });

  const emitCustomerAndVehicleInfo = (
    customer: StripeCustomer,
    vehicles: Vehicle[]
  ) =>
    emitSessionEvent({
      type: SessionEventEnum.CUSTOMER_AND_VEHICLE_INFO,
      message: {
        customer,
        vehicles
      }
    });

  const waypointFlattener = (waypoint: Waypoint): FlatWaypoint => {
    const { date, ...rest } = waypoint;

    return {
      ...rest,
      date: date
        ? `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
        : undefined
    };
  };

  const emitPickupInfo = (pickup: Waypoint) =>
    emitSessionEvent(
      {
        type: SessionEventEnum.PICKUP_INFO,
        message: pickup
      },
      waypointFlattener
    );

  const emitDeliveryInfo = (delivery: Waypoint) =>
    emitSessionEvent(
      {
        type: SessionEventEnum.DELIVERY_INFO,
        message: delivery
      },
      waypointFlattener
    );

  const emitHeight = () =>
    emitSessionEvent({
      type: SessionEventEnum.HEIGHT,
      message: document.documentElement.offsetHeight + 2
    });

  const updateHost = () => {
    if (document.documentElement.offsetHeight === lastHeight) {
      return;
    }

    lastHeight = document.documentElement.offsetHeight;
    emitHeight();
  };

  const emitPaymentSubmitted = () =>
    emitSessionEvent({
      type: SessionEventEnum.PAYMENT_SUBMITTED,
      message: undefined
    });

  const emitPaymentFailure = () =>
    emitSessionEvent({
      type: SessionEventEnum.PAYMENT_FAILURE,
      message: undefined
    });

  const emitOrderSubmitted = () =>
    emitSessionEvent({
      type: SessionEventEnum.ORDER_SUBMITTED,
      message: undefined
    });

  const emitOrderSuccess = () => {
    emitSessionEvent({
      type: SessionEventEnum.ORDER_SUCCESS,
      message: undefined
    });
    emitSessionEvent({
      type: SessionEventEnum.PAYMENT_SUCCESS,
      message: undefined
    });
  };

  const emitOrderFailure = () =>
    emitSessionEvent({
      type: SessionEventEnum.ORDER_FAILURE,
      message: undefined
    });

  const emitQuoteBooked = () =>
    emitSessionEvent({
      type: SessionEventEnum.QUOTE_BOOKED,
      message: undefined
    });

  const emitQuoteNew = () =>
    emitSessionEvent({
      type: SessionEventEnum.QUOTE_NEW,
      message: undefined
    });

  const emitNewQuoteAfterPayment = () =>
    emitSessionEvent({
      type: SessionEventEnum.NEW_QUOTE_AFTER_PAYMENT,
      message: {
        parentOriginPathname: import.meta.env.VITE_PARENT_ORIGIN_PATHNAME
      }
    });

  const emitPriceUpdated = (price: number) =>
    emitSessionEvent({
      type: SessionEventEnum.PRICE_UPDATED,
      message: price
    });

  const emitTrailerTypeUpdated = (trailerType: string) =>
    emitSessionEvent({
      type: SessionEventEnum.TRAILER_TYPE_UPDATED,
      message: trailerType
    });

  const emitVehiclesUpdated = (vehicles: Partial<Vehicle>[]) =>
    emitSessionEvent({
      type: SessionEventEnum.UPDATE_VEHICLE_INFO,
      message: { vehicles }
    });

  const isWidthEvent = (event: SessionEvent<unknown>): event is WidthEvent => {
    return (
      event.type === SessionEventEnum.WIDTH && typeof event.message === 'number'
    );
  };

  let lastWidth = 0;

  const handleSessionMessage = <E extends SessionEvent<T>, T>(
    messageEvent: MessageEvent<E>
  ) => {
    const event = messageEvent.data;

    if (isWidthEvent(event as WidthEvent)) {
      const nextWidth = event.message as number;

      if (nextWidth === lastWidth) {
        return;
      }

      lastWidth = nextWidth;
      emitHeight();
    }
  };

  return {
    events: {
      updateHost,
      handleSessionMessage,
      emitQuoteRequested,
      emitQuoteReceived,
      emitQuoteAccepted,
      emitQuoteRejected,
      emitQuoteBooked,
      emitQuoteNew,
      emitNewQuoteAfterPayment,
      emitCustomerAndVehicleInfo,
      emitPickupInfo,
      emitDeliveryInfo,
      emitPaymentSubmitted,
      emitOrderSubmitted,
      emitPaymentFailure,
      emitOrderSuccess,
      emitOrderFailure,
      emitPriceUpdated,
      emitTrailerTypeUpdated,
      emitVehiclesUpdated
    }
  };
};

const eventClient = createEventClient();

export const { events } = eventClient;
